import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { RefreshCw, Save, Loader2, Copy, Info, Globe, CheckCircle2, AlertCircle, Clock, ExternalLink, Mail } from "lucide-react";

const ROLES = ["Registrant", "Tech", "Admin", "AuxBilling"];

const REQUIRED_FIELDS = [
  { key: "FirstName", label: "First Name", required: true, col: 6 },
  { key: "LastName", label: "Last Name", required: true, col: 6 },
  { key: "OrganizationName", label: "Organization", required: false, col: 6 },
  { key: "JobTitle", label: "Job Title", required: false, col: 6 },
  { key: "Address1", label: "Address Line 1", required: true, col: 12 },
  { key: "Address2", label: "Address Line 2", required: false, col: 12 },
  { key: "City", label: "City", required: true, col: 4 },
  { key: "StateProvince", label: "State / Province", required: true, col: 4 },
  { key: "PostalCode", label: "Postal Code", required: true, col: 4 },
  { key: "Country", label: "Country (ISO-2)", required: true, col: 4, placeholder: "US" },
  { key: "Phone", label: "Phone", required: true, col: 4, placeholder: "+1.5555555555" },
  { key: "PhoneExt", label: "Phone Ext", required: false, col: 4 },
  { key: "Fax", label: "Fax", required: false, col: 6, placeholder: "+1.5555555556" },
  { key: "EmailAddress", label: "Email", required: true, col: 6, type: "email" },
];

const emptyContact = () =>
  REQUIRED_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const normalize = (raw) => {
  // Namecheap returns keys: FirstName, LastName, ...; sometimes with attributes
  const out = emptyContact();
  if (!raw || typeof raw !== "object") return out;
  for (const f of REQUIRED_FIELDS) {
    const v = raw[f.key];
    if (typeof v === "string") out[f.key] = v;
    else if (v && typeof v === "object" && "#text" in v) out[f.key] = v["#text"];
    else out[f.key] = "";
  }
  return out;
};

const validate = (c) => {
  for (const f of REQUIRED_FIELDS) {
    if (f.required && !String(c[f.key] || "").trim()) {
      return `Field "${f.label}" is required`;
    }
  }
  if (c.Country && c.Country.length !== 2) return "Country must be ISO-2 (e.g., US, UA, DE)";
  if (c.Phone && !/^\+\d{1,3}\.\d{6,15}$/.test(c.Phone))
    return "Phone format: +CC.NNNNNNNNNN (e.g., +1.5555555555)";
  return null;
};

const COL_CLASS = {
  4: "sm:col-span-4",
  6: "sm:col-span-6",
  12: "sm:col-span-12",
};

const ContactForm = ({ value, onChange, idPrefix }) => (
  <div className="grid grid-cols-12 gap-3">
    {REQUIRED_FIELDS.map((f) => (
      <div key={f.key} className={`col-span-12 ${COL_CLASS[f.col] || "sm:col-span-6"}`}>
        <Label htmlFor={`${idPrefix}-${f.key}`} className="text-xs uppercase tracking-wider">
          {f.label}{f.required && <span className="text-[var(--error)] ml-0.5">*</span>}
        </Label>
        <Input
          id={`${idPrefix}-${f.key}`}
          data-testid={`${idPrefix}-${f.key}`}
          type={f.type || "text"}
          value={value[f.key] || ""}
          onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
          placeholder={f.placeholder || ""}
          className="mono mt-1.5 rounded-sm h-9"
        />
      </div>
    ))}
  </div>
);

// Fields to compare visually between Namecheap and public RDAP
const COMPARE_FIELDS = [
  { key: "FullName", label: "Full Name", from: (c) => `${c.FirstName || ""} ${c.LastName || ""}`.trim() },
  { key: "Organization", label: "Organization", from: (c) => c.OrganizationName || "" },
  { key: "Email", label: "Email", from: (c) => c.EmailAddress || "" },
  { key: "Phone", label: "Phone", from: (c) => c.Phone || "" },
  { key: "Address1", label: "Address", from: (c) => c.Address1 || "" },
  { key: "City", label: "City", from: (c) => c.City || "" },
  { key: "StateProvince", label: "State", from: (c) => c.StateProvince || "" },
  { key: "PostalCode", label: "Postal Code", from: (c) => c.PostalCode || "" },
  { key: "Country", label: "Country", from: (c) => c.Country || "" },
];

const normStr = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");

// Critical ICANN fields that trigger 5–7 day pending change of registrant
const CRITICAL_KEYS = ["FullName", "Organization", "Email"];

const PendingRegistrantChange = ({ rdap, ncContacts, domain, loading, onRecheck }) => {
  if (!rdap || loading) return null;

  const ncRegistrant = ncContacts?.Registrant || {};
  const pubRegistrant = rdap?.contacts?.Registrant || {};
  // If RDAP didn't return any contact (heavily redacted gTLD), we can't compare
  if (!Object.keys(pubRegistrant).length) return null;

  const diffs = COMPARE_FIELDS.filter((f) => CRITICAL_KEYS.includes(f.key))
    .map((f) => ({ ...f, ncVal: f.from(ncRegistrant), pubVal: pubRegistrant[f.key] || "" }))
    .filter((r) => r.ncVal && r.pubVal && normStr(r.ncVal) !== normStr(r.pubVal));

  if (diffs.length === 0) return null;

  const lastChanged = rdap?.last_changed ? new Date(rdap.last_changed) : null;
  const ageDays = lastChanged ? Math.floor((Date.now() - lastChanged.getTime()) / 86400000) : null;
  const sharingUrl = `https://ap.www.namecheap.com/Domains/DomainControlPanel/${encodeURIComponent(domain)}/sharing`;

  // Emails of both sides (old + new) — user needs to confirm on both
  const oldEmail = pubRegistrant.Email || "";
  const newEmail = ncRegistrant.EmailAddress || "";

  return (
    <Card className="rounded-sm shadow-none border-amber-300 bg-amber-50 mt-4" data-testid="pending-change-widget">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-amber-900">
                Pending ICANN verification
              </h3>
              {ageDays !== null && (
                <span className="text-[11px] mono px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800 uppercase tracking-wider">
                  {ageDays === 0 ? "Today" : ageDays === 1 ? "1 day ago" : `${ageDays} days ago`}
                </span>
              )}
            </div>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              You changed <strong>{diffs.map((d) => d.label).join(", ")}</strong> at Namecheap, but the
              public WHOIS still shows the old data. ICANN (IRTP-C policy) requires confirmation by email
              from <strong>both</strong> the old and the new registrant within 5–7 days. Until both confirm,
              the registry keeps the old data.
            </p>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mono">
              {oldEmail && (
                <div className="rounded-sm bg-white border border-amber-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Old email (must approve)
                  </div>
                  <div className="break-all">{oldEmail}</div>
                </div>
              )}
              {newEmail && (
                <div className="rounded-sm bg-white border border-amber-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> New email (must approve)
                  </div>
                  <div className="break-all">{newEmail}</div>
                </div>
              )}
            </div>

            <div className="mt-3 text-[11px] text-amber-800/80">
              ⓘ Check the <strong>Spam / Promotions</strong> folder. Look for an email from{" "}
              <span className="mono">noreply@namecheap.com</span> with subject "Change of Registrant approval required".
            </div>

            <div className="mt-4 flex gap-2 flex-wrap">
              <a href={sharingUrl} target="_blank" rel="noreferrer" data-testid="link-namecheap-sharing">
                <Button size="sm" className="brand-bg hover:brand-bg text-white rounded-sm h-8">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  Open Sharing & Transfer
                </Button>
              </a>
              <Button
                data-testid="btn-recheck-pending"
                variant="outline"
                size="sm"
                className="rounded-sm h-8 bg-white"
                onClick={onRecheck}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Recheck status
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RdapComparison = ({ rdap, ncContacts, loading, error, onRefresh, domain }) => {
  if (loading && !rdap) {
    return (
      <Card className="rounded-sm shadow-none border-[var(--border)] mt-4">
        <CardContent className="p-6 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading public WHOIS (RDAP)…
        </CardContent>
      </Card>
    );
  }
  if (error && !rdap) {
    return (
      <Card className="rounded-sm shadow-none border-[var(--border)] mt-4">
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <AlertCircle className="w-4 h-4" /> Public WHOIS lookup failed: <span className="mono">{error}</span>
          </div>
          <Button data-testid="btn-refresh-rdap" variant="outline" size="sm" onClick={onRefresh} className="rounded-sm">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!rdap) return null;

  const ncRegistrant = ncContacts?.Registrant || {};
  const pubRegistrant = rdap?.contacts?.Registrant || {};
  const rows = COMPARE_FIELDS.map((f) => {
    const ncVal = f.from(ncRegistrant);
    const pubVal = pubRegistrant[f.key] || "";
    const both = ncVal && pubVal;
    const match = both && normStr(ncVal) === normStr(pubVal);
    return { ...f, ncVal, pubVal, match, both };
  });
  const matchedCount = rows.filter((r) => r.match).length;
  const comparableCount = rows.filter((r) => r.both).length;
  const allMatch = comparableCount > 0 && matchedCount === comparableCount;

  return (
    <Card className="rounded-sm shadow-none border-[var(--border)] mt-4" data-testid="rdap-comparison">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[var(--text-secondary)]" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              Public WHOIS (RDAP — same source as lookup.icann.org)
            </div>
          </div>
          <div className="flex items-center gap-3">
            {comparableCount > 0 && (
              <span
                className={`text-xs font-medium px-2 py-1 rounded-sm ${
                  allMatch ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                }`}
                data-testid="rdap-match-summary"
              >
                {allMatch ? (
                  <><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Propagated · {matchedCount}/{comparableCount}</>
                ) : (
                  <><AlertCircle className="w-3.5 h-3.5 inline mr-1" /> Not yet · {matchedCount}/{comparableCount} match</>
                )}
              </span>
            )}
            <Button data-testid="btn-refresh-rdap" variant="outline" size="sm" onClick={onRefresh} className="rounded-sm" disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Recheck
            </Button>
          </div>
        </div>

        {rdap?.last_changed && (
          <div className="text-xs text-[var(--text-secondary)] mb-3 mono">
            Last changed in registry: <span className="text-[var(--text-primary)]">{rdap.last_changed}</span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="rdap-comparison-table">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left py-2 px-2 text-[11px] uppercase tracking-wider text-[var(--text-secondary)] w-1/4">Field</th>
                <th className="text-left py-2 px-2 text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">Namecheap (Registrant)</th>
                <th className="text-left py-2 px-2 text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">Public WHOIS</th>
                <th className="text-center py-2 px-2 text-[11px] uppercase tracking-wider text-[var(--text-secondary)] w-16">Match</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-[var(--border)]" data-testid={`rdap-row-${r.key}`}>
                  <td className="py-2 px-2 text-xs uppercase tracking-wider text-[var(--text-secondary)]">{r.label}</td>
                  <td className="py-2 px-2 mono break-all">{r.ncVal || <span className="text-[var(--text-secondary)]">—</span>}</td>
                  <td className={`py-2 px-2 mono break-all ${r.both && !r.match ? "bg-amber-50" : ""}`}>
                    {r.pubVal || <span className="text-[var(--text-secondary)]">—</span>}
                  </td>
                  <td className="py-2 px-2 text-center">
                    {r.both ? (
                      r.match ? (
                        <CheckCircle2 className="w-4 h-4 inline text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 inline text-amber-600" />
                      )
                    ) : (
                      <span className="text-[var(--text-secondary)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-[var(--text-secondary)]">
          <span>
            Tip: registry propagation usually takes 5–60 minutes. Public WHOIS aggregators (who.is, whois.com) may cache up to 24h.
          </span>
          <a
            href={`https://lookup.icann.org/en/lookup?q=${encodeURIComponent(domain)}`}
            target="_blank" rel="noreferrer"
            className="underline mono"
            data-testid="link-icann-lookup"
          >
            Open in lookup.icann.org →
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const WhoisUpdate = () => {
  const [params] = useSearchParams();
  const [domain, setDomain] = useState(params.get("domain") || "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncAll, setSyncAll] = useState(true);
  const [contacts, setContacts] = useState({
    Registrant: emptyContact(),
    Tech: emptyContact(),
    Admin: emptyContact(),
    AuxBilling: emptyContact(),
  });
  const [loaded, setLoaded] = useState(false);
  const [rdap, setRdap] = useState(null);
  const [rdapLoading, setRdapLoading] = useState(false);
  const [rdapError, setRdapError] = useState("");

  const loadRdap = async (d) => {
    const dom = (d || domain).trim().toLowerCase();
    if (!dom.includes(".")) return;
    setRdapLoading(true);
    setRdapError("");
    try {
      const { data } = await api.get(`/rdap/${encodeURIComponent(dom)}`);
      setRdap(data);
    } catch (e) {
      setRdap(null);
      setRdapError(e?.response?.data?.detail || "RDAP lookup failed");
    } finally {
      setRdapLoading(false);
    }
  };

  const load = async () => {
    if (!domain.includes(".")) {
      toast.error("Enter a valid domain");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/domains/contacts/${encodeURIComponent(domain.trim().toLowerCase())}`);
      setContacts({
        Registrant: normalize(data.Registrant),
        Tech: normalize(data.Tech),
        Admin: normalize(data.Admin),
        AuxBilling: normalize(data.AuxBilling),
      });
      setLoaded(true);
      toast.success(`Loaded WHOIS for ${domain}`);
      // Kick off public RDAP lookup in parallel (don't block UI on it)
      loadRdap(domain);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const updateRole = (role) => (next) => {
    if (syncAll) {
      setContacts({ Registrant: next, Tech: next, Admin: next, AuxBilling: next });
    } else {
      setContacts((cur) => ({ ...cur, [role]: next }));
    }
  };

  const copyFromRegistrant = (toRole) => {
    setContacts((cur) => ({ ...cur, [toRole]: { ...cur.Registrant } }));
    toast.success(`Copied Registrant → ${toRole}`);
  };

  const save = async () => {
    for (const role of ROLES) {
      const err = validate(contacts[role]);
      if (err) {
        toast.error(`${role}: ${err}`);
        return;
      }
    }
    setSaving(true);
    try {
      const body = syncAll
        ? { domain_name: domain, contact: contacts.Registrant }
        : { domain_name: domain, contacts };
      await api.post("/domains/contacts", body);
      toast.success(`WHOIS updated for ${domain}. Namecheap may send a verification email.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Compliance"
        title="WHOIS Update"
        description="Edit registrant / technical / admin / billing contacts for a domain you own. Calls namecheap.domains.setContacts."
      />
      <Section>
        <Card className="rounded-sm shadow-none border-[var(--border)]">
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[240px]">
                <Label htmlFor="whois-domain" className="text-xs uppercase tracking-wider">
                  Domain
                </Label>
                <Input
                  data-testid="whois-update-domain-input"
                  id="whois-domain"
                  className="mono mt-1.5 rounded-sm"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value.trim().toLowerCase())}
                  placeholder="example.com"
                  onKeyDown={(e) => e.key === "Enter" && load()}
                />
              </div>
              <Button
                data-testid="btn-load-contacts"
                onClick={load}
                disabled={loading || !domain.includes(".")}
                className="brand-bg hover:brand-bg text-white rounded-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Load
              </Button>
            </div>
          </CardContent>
        </Card>

        {loaded && (
          <>
            <Alert className="mt-4 rounded-sm border-[var(--border)] bg-[#FAFAFA]" data-testid="whois-info">
              <Info className="w-4 h-4" />
              <AlertDescription className="text-sm text-[var(--text-secondary)]">
                Updating Registrant <strong>email</strong>, <strong>first/last name</strong> or <strong>organization</strong> may
                trigger ICANN's 60-day transfer lock and a confirmation email. Other changes apply immediately.
              </AlertDescription>
            </Alert>

            <div className="mt-4 flex items-center justify-between rounded-sm border border-[var(--border)] bg-white p-4">
              <div>
                <div className="text-sm font-medium">Use the same contact for all 4 roles</div>
                <div className="text-xs text-[var(--text-secondary)]">
                  Recommended. Most owners use identical Registrant / Tech / Admin / AuxBilling.
                </div>
              </div>
              <Switch
                data-testid="toggle-sync-roles"
                checked={syncAll}
                onCheckedChange={setSyncAll}
              />
            </div>

            {syncAll ? (
              <Card className="rounded-sm shadow-none border-[var(--border)] mt-4">
                <CardContent className="p-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-4">
                    Shared contact (applied to Registrant + Tech + Admin + AuxBilling)
                  </div>
                  <ContactForm
                    value={contacts.Registrant}
                    onChange={updateRole("Registrant")}
                    idPrefix="shared"
                  />
                </CardContent>
              </Card>
            ) : (
              <Tabs defaultValue="Registrant" className="mt-4">
                <TabsList className="rounded-sm">
                  {ROLES.map((r) => (
                    <TabsTrigger key={r} value={r} data-testid={`tab-${r}`}>{r}</TabsTrigger>
                  ))}
                </TabsList>
                {ROLES.map((role) => (
                  <TabsContent key={role} value={role}>
                    <Card className="rounded-sm shadow-none border-[var(--border)]">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
                            {role} contact
                          </div>
                          {role !== "Registrant" && (
                            <Button
                              data-testid={`btn-copy-${role}`}
                              variant="outline"
                              size="sm"
                              onClick={() => copyFromRegistrant(role)}
                              className="rounded-sm"
                            >
                              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy from Registrant
                            </Button>
                          )}
                        </div>
                        <ContactForm
                          value={contacts[role]}
                          onChange={updateRole(role)}
                          idPrefix={role.toLowerCase()}
                        />
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            )}

            <div className="mt-4 flex justify-end">
              <Button
                data-testid="btn-save-contacts"
                onClick={save}
                disabled={saving}
                className="brand-bg hover:brand-bg text-white rounded-sm"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Update WHOIS
              </Button>
            </div>

            <PendingRegistrantChange
              rdap={rdap}
              ncContacts={contacts}
              domain={domain}
              loading={rdapLoading}
              onRecheck={() => loadRdap()}
            />

            <RdapComparison
              rdap={rdap}
              ncContacts={contacts}
              loading={rdapLoading}
              error={rdapError}
              onRefresh={() => loadRdap()}
              domain={domain}
            />
          </>
        )}
      </Section>
    </div>
  );
};

export default WhoisUpdate;
